import { AUTO_LOAD_ZOOM } from '../config';
import { useIsTouch } from '../hooks/useMediaQuery';
import type { CanopyStats, ShadeMode } from '../lib/types';

/**
 * Tree counts per borough, from pipeline/out/tree-counts.json. The source data
 * was rendered once in 2023 and will not be regenerated, so these are constants
 * rather than a fetch.
 */
export const BOROUGH_TREE_COUNTS = {
  bronx: 85203,
  brooklyn: 177293,
  manhattan: 65423,
  queens: 250551,
  staten: 105318,
} as const;

export const TOTAL_TREES = Object.values(BOROUGH_TREE_COUNTS).reduce((a, b) => a + b, 0);

/** Progress of the viewport's canopy fetch, in trees. See App's loadProgress. */
export interface LoadProgress {
  /** Requests finished, whether they yielded a canopy, an empty set, or a 404. */
  loaded: number;
  /** Trees this camera asked for, at most AUTO_TREE_CAP. */
  total: number;
}

interface Props {
  stats: CanopyStats;
  zoom: number;
  shadeMode: ShadeMode;
  /** Non-null only while canopies are outstanding for the current camera. */
  loading: LoadProgress | null;
  /** True when the canopy shown is the trees the user picked, not the viewport. */
  individualSelection: boolean;
  /**
   * `panel` floats over the map as its own card. `flow` drops the panel
   * chrome and positioning so the phone sheet can stack this with its
   * siblings, and hides the count/bar the sheet's peek row already shows.
   * `inline` also drops the chrome, but keeps the count/bar -- for stacking
   * under the masthead title as one merged card, where nothing else on
   * screen shows them.
   */
  variant?: 'panel' | 'flow' | 'inline';
}

const pct = (n: number, total: number) => (total === 0 ? 0 : (n / total) * 100);
const fmtPct = (n: number, total: number) =>
  total === 0 ? '—' : `${pct(n, total).toFixed(1)}%`;

/** The stacked proportion bar, shared by the panel and the sheet's peek row. */
function ProportionBar({ stats }: { stats: CanopyStats }) {
  const total = stats.other + stats.shading + stats.shaded;
  return (
    <div
      className="bar"
      role="img"
      aria-label={`Canopy distribution: ${fmtPct(stats.other, total)} shading ground, ${fmtPct(stats.shading, total)} shading buildings, ${fmtPct(stats.shaded, total)} in shade`}
    >
      <span className="bar__seg bar__seg--other" style={{ width: `${pct(stats.other, total)}%` }} />
      <span
        className="bar__seg bar__seg--shading"
        style={{ width: `${pct(stats.shading, total)}%` }}
      />
      <span
        className="bar__seg bar__seg--shaded"
        style={{ width: `${pct(stats.shaded, total)}%` }}
      />
    </div>
  );
}

/**
 * The filling track, shared by the panel's bar and the sheet's peek row.
 *
 * Determinate rather than a spinner, because the total is known before the
 * first request goes out: the camera settles, the cap takes at most
 * AUTO_TREE_CAP trees nearest the centre, and that number does not move again
 * until the camera does. A view can be a couple of hundred separate files over
 * a slow link, and at that length "still working" is not the useful thing to
 * say -- how much of the wait is left is.
 */
function LoadingTrack({ loading }: { loading: LoadProgress }) {
  const done = loading.total === 0 ? 0 : (loading.loaded / loading.total) * 100;
  return (
    <div
      className="loadbar__track"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={loading.total}
      aria-valuenow={loading.loaded}
      aria-label={`Loading canopies: ${loading.loaded} of ${loading.total}`}
    >
      <span className="loadbar__fill" style={{ width: `${done}%` }} />
    </div>
  );
}

/**
 * The one line the phone sheet shows while collapsed.
 *
 * It has to answer "what am I looking at" in the space of a row, so it is the
 * count plus the proportion bar -- the bar carries the whole breakdown at a
 * glance without spending a line per class. The named percentages are in the
 * sheet a tap below.
 */
export function StatsSummary({
  stats,
  zoom,
  shadeMode,
  loading,
}: Pick<Props, 'stats' | 'zoom' | 'shadeMode' | 'loading'>) {
  if (stats.trees === 0) {
    // Before the first batch classifies there is no count to show, so the row
    // falls back to the city total -- except while loading, where the honest
    // number is how far along this view is.
    return (
      <span className="summary">
        <span className="summary__count">
          {loading ? loading.loaded.toLocaleString() : TOTAL_TREES.toLocaleString()}
        </span>
        <span className="summary__unit">
          {loading
            ? `of ${loading.total.toLocaleString()} canopies…`
            : zoom >= AUTO_LOAD_ZOOM
              ? 'street trees — loading canopies…'
              : 'street trees'}
        </span>
        {loading && <LoadingTrack loading={loading} />}
      </span>
    );
  }

  return (
    <span className="summary">
      <span className="summary__count">{stats.trees.toLocaleString()}</span>
      <span className="summary__unit">{stats.trees === 1 ? 'tree' : 'trees'}</span>
      {/* One slot, and while trees are still landing the load is what belongs
          in it: the proportions are a breakdown of an incomplete set, and they
          shift under the reader every time a batch commits. The peek row has
          space for one bar, so it shows the one that is not about to change. */}
      {loading ? (
        <LoadingTrack loading={loading} />
      ) : (
        shadeMode === 'analysis' && <ProportionBar stats={stats} />
      )}
    </span>
  );
}

export function StatsPanel({
  stats,
  shadeMode,
  loading,
  individualSelection,
  variant = 'panel',
}: Props) {
  const total = stats.other + stats.shading + stats.shaded;

  // Naming a gesture the device cannot perform is the tell that a UI was
  // written for something else. Everything here is one word either way.
  const tap = useIsTouch();
  const Verb = tap ? 'Tap' : 'Click';
  const verb = tap ? 'tap' : 'click';

  // Which trees the figures describe. Loading the whole viewport is the default
  // and cannot be turned off, so it has to be stated rather than inferred --
  // otherwise a reading of "1,204 trees" looks like a selection nobody made.
  const scopeNote = individualSelection
    ? `Showing ${stats.trees} selected ${stats.trees === 1 ? 'tree' : 'trees'}. ${Verb} another to add it, or Clear selection to return to the whole view.`
    : `Showing every tree in the entire viewable area — ${verb} one to isolate it.`;

  return (
    <section
      className={
        variant === 'panel'
          ? 'stats panel enter enter--2'
          : variant === 'inline'
            ? 'stats stats--inline'
            : 'stats stats--flow'
      }
      aria-label="Canopy statistics"
    >
      {/* Above the figures rather than below them, because it is the caveat on
          everything that follows: while this is on screen, every number in the
          panel describes a fraction of the view. */}
      {loading && (
        <div className="loadbar">
          <div className="loadbar__row">
            <span className="panel__label">Loading canopies</span>
            <span className="loadbar__count">
              {loading.loaded.toLocaleString()} / {loading.total.toLocaleString()}
            </span>
          </div>
          <LoadingTrack loading={loading} />
        </div>
      )}

      {stats.trees === 0 ? (
        <>
          <div className="panel__label" style={{ marginBottom: 10 }}>
            Current View
          </div>
          <div className="stats__count">
            {TOTAL_TREES.toLocaleString()}
            <small>street trees</small>
          </div>
        </>
      ) : (
        <>
          <div className="stats__head">
            <div>
              <div className="panel__label" style={{ marginBottom: 5 }}>
                Selected canopy
              </div>
              <div className="stats__count">
                {stats.trees}
                <small>{stats.trees === 1 ? 'tree' : 'trees'}</small>
              </div>
              <div className="stats__sub">{stats.points.toLocaleString()} pts</div>
            </div>

            {/* The second half of the analysis, and until now the invisible
                half: clicking a building narrows every percentage below to
                that building alone. That was stated in prose at the bottom of
                the panel and nowhere else, so the counterpart of the tree
                count -- how many buildings the figures are measured against,
                and whether that set is the user's choice or just what is on
                screen -- now sits beside it, in the accent the selected
                massing takes on the map. */}
            {shadeMode === 'analysis' && (
              <div className={`stats__target${stats.focused ? ' is-focused' : ''}`}>
                <div className="panel__label" style={{ marginBottom: 5 }}>
                  Measured against
                </div>
                <div className="stats__subcount">
                  {stats.buildings.toLocaleString()}
                  <small>{stats.buildings === 1 ? 'building' : 'buildings'}</small>
                </div>
                <div className="stats__sub">
                  {stats.focused ? (tap ? 'you tapped' : 'you clicked') : 'all in view'}
                </div>
              </div>
            )}
          </div>

          {/* With the analysis hidden the canopy is a single flat green, so the
              breakdown would be a legend for colors that are not on screen.
              The figures are still true, but showing them here would invite
              reading them off a picture that no longer encodes them. */}
          {shadeMode === 'analysis' ? (
            <>
              <ProportionBar stats={stats} />

              <div className="legend">
                <div className="legend__row">
                  <span className="legend__dot legend__dot--other" />
                  <span className="legend__name">Shading ground</span>
                  <span className="legend__value">{fmtPct(stats.other, total)}</span>
                </div>
                <div className="legend__row">
                  <span className="legend__dot legend__dot--shading" />
                  <span className="legend__name">Shading buildings</span>
                  <span className="legend__value">{fmtPct(stats.shading, total)}</span>
                </div>
                <div className="legend__row">
                  <span className="legend__dot legend__dot--shaded" />
                  <span className="legend__name">Canopy in shade</span>
                  <span className="legend__value">{fmtPct(stats.shaded, total)}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="legend">
              <div className="legend__row">
                <span className="legend__dot legend__dot--foliage" />
                <span className="legend__name">Canopy</span>
              </div>
            </div>
          )}

          <p className="stats__note">
            {scopeNote}
            {shadeMode === 'analysis' ? (
              <>
                {' '}
                {stats.focused
                  ? `The percentages above count only the ${stats.buildings} ${
                      stats.buildings === 1 ? 'building' : 'buildings'
                    } you picked — ${verb} ${stats.buildings === 1 ? 'it' : 'one'} again to release it.`
                  : stats.buildings > 0
                    ? `The percentages above count every building in view — ${verb} one to measure the canopy against it alone.`
                    : 'No buildings in view to measure against.'}
              </>
            ) : (
              <> Shade analysis hidden.</>
            )}
          </p>

          {/* The height-aware model is the default and the only one a visitor
              can reach without editing the URL, so naming it on every view was
              a footnote about an alternative nobody had asked for. It is worth
              saying only in the state the reader opted into: ?model=2023
              reproduces the published figures, and that reading is not
              continuous with the default one. */}
          {shadeMode === 'analysis' && stats.model === '2023' && (
            <p className="stats__model">
              Measured with the <strong>2023 model</strong>, which evaluates every canopy point
              as if it stood at ground level. Drop <code>?model=2023</code> from the URL for
              height-aware occlusion.
            </p>
          )}
        </>
      )}
    </section>
  );
}
